import React, { useEffect, useRef } from 'react'
import styled from 'styled-components'

// Timing constants for screen reader announcements
const ANNOUNCEMENT_DELAY_MS = 50 // Delay before setting new announcement
const CLEAR_ANNOUNCEMENT_DELAY_MS = 500 // Time to wait before clearing announcement

// Global announcer instance
let announceFunction = null

/**
 * Register the announce function
 * @param {Function} fn - The function to call for announcements
 */
export const registerAnnouncer = (fn) => {
  announceFunction = fn
}

/**
 * Make an announcement to screen readers
 * @param {string} message - The message to announce
 * @param {string} priority - 'polite' or 'assertive'
 */
export const announce = (message, priority = 'polite') => {
  if (announceFunction) {
    announceFunction(message, priority)
  }
}

/**
 * LiveRegionAnnouncer Component
 * This should be mounted once at the root of your app
 * It creates persistent live regions for screen reader announcements
 */
const LiveRegionAnnouncer = () => {
  const politeRef = useRef(null)
  const assertiveRef = useRef(null)

  useEffect(() => {
    // Keep track of active timeouts for cleanup
    const timeouts = []

    // Register the announce function
    registerAnnouncer((message, priority) => {
      const targetRef = priority === 'assertive' ? assertiveRef : politeRef
      if (targetRef.current) {
        // Clear first to ensure announcement triggers even with same text
        targetRef.current.textContent = ''

        // Set the new message after a brief delay
        const setMessageTimeout = setTimeout(() => {
          if (targetRef.current) {
            targetRef.current.textContent = message

            // Clear the message after it's been announced
            const clearMessageTimeout = setTimeout(() => {
              if (targetRef.current) {
                targetRef.current.textContent = ''
              }
            }, CLEAR_ANNOUNCEMENT_DELAY_MS)

            timeouts.push(clearMessageTimeout)
          }
        }, ANNOUNCEMENT_DELAY_MS)

        timeouts.push(setMessageTimeout)
      }
    })

    // Cleanup on unmount
    return () => {
      // Clear all pending timeouts
      timeouts.forEach((timeout) => clearTimeout(timeout))
      registerAnnouncer(null)
    }
  }, [])

  return (
    <>
      <VisuallyHidden
        ref={politeRef}
        role="status"
        aria-live="polite"
        aria-atomic="true"
      />
      <VisuallyHidden
        ref={assertiveRef}
        role="alert"
        aria-live="assertive"
        aria-atomic="true"
      />
    </>
  )
}

export default LiveRegionAnnouncer

const VisuallyHidden = styled.div`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`
